import { useState, useRef, Fragment, useEffect } from 'react';
import { connect } from 'react-redux';
import { useRecoilState } from 'recoil';
import { Col, Form, Input, Row, Typography, notification } from 'antd';
import { titleCase } from 'voca';

import { ImageSelector } from '../../../components/file-uploader';
import { getGroupExercises } from '../actions/groupExercises';
import {
  popDescription,
  popName,
  popParameters,
  popTag,
  popVideo
} from './pops';
import { exerciseImgValidator } from '../validators/groupExercises';
import { toCamelCaseObjKeys } from '../../../utils/object.utils';

import states from '../../../states';
import services from '../../../services';

import ModalForm from '../../../components/ModalForm';
import ProfileImage from '../../patients/Modals/ProfileImage';

const YOUTUBE_REGEX =
  /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=|\?v=)([^#\&\?]*).*/;

const toBase64 = (file) => {
  return new Promise((resolve) => {
    const reader = new FileReader();

    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
  });
};

const ClinicExerciseForm = ({
  visibleProfile,
  getGroupExercises,
  values,
  isOpen,
  onCancel
}) => {
  const [loading, setLoading] = useState(false);
  const [parametersError, setParametersError] = useState(false);
  const [formData, setFormData] = useState({
    validImage: true,
    img: null,
    file: null,
    cropping: false,
    removeFile: null,
    base64: null
  });

  const [exercises, setExercises] = useRecoilState(states.exercises);

  const [form] = Form.useForm();
  const imgSelectorRef = useRef();

  useEffect(() => {
    if (isOpen) {
      form.setFieldsValue({
        exerciseName: titleCase(values.ExerciseName || ''),
        sets: values.Sets || '',
        reps: values.Reps || '',
        hold: values.Hold || '',
        tags: values.Tag || '',
        instructions: values.Instructions || '',
        videoUrl: values.Video || ''
      });

      setFormData((prevState) => ({
        ...prevState,
        img: values.image || values.imageUrl || null
      }));
    }
  }, [isOpen]);

  const onFileSelected = async (file, errors) => {
    const isValid = _.isEmpty(errors);
    const imgFile = isValid ? await toBase64(file) : null;

    setFormData((prevState) => ({
      ...prevState,
      validImage: isValid,
      cropping: isValid,
      file: isValid ? file : null,
      base64: imgFile
    }));
  };

  const onFileRemoved = () => {
    setFormData((prevState) => ({
      ...prevState,
      validImage: true,
      removeFile: null
    }));
  };

  const handleImageCrop = (file, base64Img) => {
    setFormData((prevState) => ({
      ...prevState,
      img: base64Img,
      file,
      cropping: false
    }));
  };

  const handleExitCrop = () => {
    setFormData((prevState) => ({
      ...prevState,
      img: null,
      cropping: false,
      file: null
    }));
  };

  const validateExerciseName = (str) => {
    if (str.length === 0) {
      return true;
    } else if (!!values?.Id) {
      return false;
    } else {
      return exercises.list.some((ex) => ex.exerciseName === str.toLowerCase());
    }
  };

  const validateVideoUrl = async (str) => {
    if (str.match(YOUTUBE_REGEX)) {
      return getYoutubeThumbnail(str).length === 11;
    } else if (str.startsWith('https://vimeo.com')) {
      return getVimeoThumbnail(str).then((videoId) => {
        return !!videoId;
      });
    } else {
      setFormData((prevState) => ({
        ...prevState,
        img: null
      }));

      return false;
    }
  };

  const getYoutubeThumbnail = (url) => {
    const match = url.match(YOUTUBE_REGEX);
    const videoId = match[2];

    setFormData((prevState) => ({
      ...prevState,
      img: `http://img.youtube.com/vi/${videoId}/0.jpg`
    }));

    return videoId;
  };

  const getVimeoThumbnail = async (url) => {
    let videoId = '';
    await fetch(`https://vimeo.com/api/oembed.json?url=${url}`).then(
      async (response) => {
        await response.json().then((data) => {
          videoId = data.video_id;

          setFormData((prevState) => ({
            ...prevState,
            img: data.thumbnail_url
          }));
        });
      }
    );

    return videoId;
  };

  const handleSubmit = async (form) => {
    try {
      setLoading(true);

      const name = form.exerciseName.trim().toLowerCase();
      const params = {
        active: true,
        Active: true,
        exerciseName: name,
        ExerciseName: name,
        GroupId: visibleProfile.GroupId,
        image: formData.base64 || formData.img,
        SearchTerm: name.replace(/\s/g, ''),
        Instructions: form.instructions,
        Reps: form.reps || '',
        Sets: form.sets || '',
        Hold: form.hold || '',
        Tag: form.tags || '',
        Video: form.videoUrl || ''
      };

      let response = null;
      if (!!values?.Id) {
        response = await services.exercises.updateClinicExercise({
          ...params,
          Id: values.Id,
          Created: values.Created
        });
      } else {
        response = await services.exercises.addClinicExercise(params);
      }

      if (response.status === 200) {
        const newExercise = {
          ...toCamelCaseObjKeys({
            Id: response.data.id,
            Active: params.Active,
            ExerciseName: params.ExerciseName,
            GroupId: params.GroupId,
            Image: params.image,
            SearchTerm: params.SearchTerm,
            Instructions: params.Instructions,
            Reps: params.Reps,
            Sets: params.Sets,
            Tag: params.Tag,
            Video: params.Video
          })
        };

        setExercises((prevState) => {
          const index = prevState.custom.exercises.length;
          const listCopy = [...prevState.list];
          const unmappedCopy = [...prevState.unmapped];

          listCopy.splice(index, 0, newExercise);
          unmappedCopy.splice(index, 0, newExercise);

          return {
            ...prevState,
            unmapped: unmappedCopy,
            list: listCopy
          };
        });

        notification.success({
          message: 'Success!',
          description: `Clinic exercise successfully ${
            !!values?.Id ? 'updated' : 'added'
          }.`
        });

        handleCancelModal();
        getGroupExercises(params.GroupId);
      } else {
        throw new Error(response);
      }
    } catch (error) {
      console.log('[clinic exercise]', error);

      notification.error({
        message: 'Error!',
        description: `An error occurred while ${
          !!values?.Id ? 'updating' : 'adding'
        } clinic exercise.`
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelModal = () => {
    form.resetFields();

    setParametersError(false);
    setFormData((prevState) => ({
      ...prevState,
      base64: null
    }));

    handleExitCrop();
    onCancel();
  };

  return (
    <Fragment>
      {formData.cropping && (
        <ProfileImage
          show={formData.cropping}
          image={formData.file}
          onSubmit={handleImageCrop}
          onExit={handleExitCrop}
          onHide={handleExitCrop}
          closeButton={handleExitCrop}
          isExerciseImage={true}
        />
      )}

      <ModalForm
        width="60%"
        form="clinicExercise"
        header={(!!values?.Id ? 'Edit' : 'Add') + ' Exercise'}
        showModal={isOpen}
        handleClose={handleCancelModal}
        handleSubmit={handleSubmit}
        loading={loading}
      >
        <Form
          layout="vertical"
          id="clinicExercise"
          form={form}
          onFinish={handleSubmit}
        >
          <Row gutter={[24, 0]}>
            <Col lg={12} md={12} sm={12}>
              <Row gutter={[16, 0]}>
                <Col lg={24} md={24} sm={24} xs={24}>
                  <Form.Item
                    name="exerciseName"
                    label="Exercise Name"
                    tooltip={popName}
                    validateTrigger="onBlur"
                    rules={[
                      {
                        required: true,
                        message: 'Please input your exercise name.'
                      },
                      { max: 256, message: 'Max length is 256 only.' },
                      {
                        message: 'Exercise name already exists.',
                        validator: (_, value) => {
                          if (!value) {
                            return Promise.resolve();
                          }

                          return validateExerciseName(value)
                            ? Promise.reject()
                            : Promise.resolve();
                        }
                      }
                    ]}
                  >
                    <Input
                      type="text"
                      size="large"
                      placeholder="Enter exercise name"
                      name="exerciseName"
                    />
                  </Form.Item>
                </Col>

                <Col lg={24} md={24} sm={24} xs={24}>
                  <Form.Item
                    style={{ marginBottom: -40, fontWeight: 'bold' }}
                    label="Parameters"
                    tooltip={popParameters}
                  />

                  <Row gutter={[16, 0]}>
                    <Col lg={8} md={8} sm={24} xs={24}>
                      <Form.Item
                        name="sets"
                        label="Sets"
                        dependencies={['reps', 'hold']}
                        rules={[
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              if (
                                !value &&
                                !getFieldValue('reps') &&
                                !getFieldValue('hold')
                              ) {
                                setParametersError(true);
                                return Promise.reject();
                              } else {
                                setParametersError(false);
                                return Promise.resolve();
                              }
                            }
                          })
                        ]}
                      >
                        <Input
                          type="text"
                          size="large"
                          placeholder="Enter sets"
                          name="sets"
                        />
                      </Form.Item>
                    </Col>

                    <Col lg={8} md={8} sm={24} xs={24}>
                      <Form.Item
                        name="reps"
                        label="Reps"
                        dependencies={['sets', 'hold']}
                        rules={[
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              if (
                                !value &&
                                !getFieldValue('sets') &&
                                !getFieldValue('hold')
                              ) {
                                setParametersError(true);
                                return Promise.reject();
                              } else {
                                setParametersError(false);
                                return Promise.resolve();
                              }
                            }
                          })
                        ]}
                      >
                        <Input
                          type="text"
                          size="large"
                          placeholder="Enter reps"
                          name="reps"
                        />
                      </Form.Item>
                    </Col>

                    <Col lg={8} md={8} sm={24} xs={24}>
                      <Form.Item
                        name="hold"
                        label="Hold"
                        dependencies={['sets', 'reps']}
                        rules={[
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              if (
                                !value &&
                                !getFieldValue('sets') &&
                                !getFieldValue('reps')
                              ) {
                                setParametersError(true);
                                return Promise.reject();
                              } else {
                                setParametersError(false);
                                return Promise.resolve();
                              }
                            }
                          })
                        ]}
                      >
                        <Input
                          type="text"
                          size="large"
                          placeholder="Enter hold"
                          name="hold"
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  {parametersError && (
                    <Typography.Paragraph
                      type="danger"
                      style={{ marginTop: -20 }}
                    >
                      Please fill in at least one parameter field.
                    </Typography.Paragraph>
                  )}
                </Col>

                <Col lg={24} md={24} sm={24} xs={24}>
                  <Form.Item name="tags" label="Tags" tooltip={popTag}>
                    <Input
                      type="text"
                      size="large"
                      placeholder="Enter tags"
                      name="tags"
                      rows={4}
                    />
                  </Form.Item>
                </Col>

                <Col lg={24} md={24} sm={24} xs={24}>
                  <Form.Item
                    name="instructions"
                    label="Instructions"
                    tooltip={popDescription}
                    rules={[
                      {
                        required: true,
                        message: 'Please input your instructions.'
                      },
                      { max: 750, message: 'Max length is 750 only.' }
                    ]}
                  >
                    <Input.TextArea
                      size="large"
                      placeholder="Enter instructions"
                      name="instructions"
                      rows={4}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Col>

            <Col lg={12} md={12} sm={12}>
              <Col lg={24} md={24} sm={24} xs={24}>
                <Form.Item
                  name="videoUrl"
                  label="Exercise Video URL"
                  tooltip={popVideo}
                  validateTrigger="onBlur"
                  rules={[
                    {
                      message: 'Please input a valid exercise video URL.',
                      validator: async (_, value) => {
                        if (!value) {
                          return Promise.resolve();
                        }

                        const valid = await validateVideoUrl(value);
                        if (valid) {
                          return Promise.resolve();
                        } else {
                          return Promise.reject();
                        }
                      }
                    }
                  ]}
                >
                  <Input
                    type="text"
                    size="large"
                    placeholder="Enter exercise video URL"
                    name="videoUrl"
                    rows={4}
                  />
                </Form.Item>
              </Col>

              <Col lg={24} md={24}>
                <Form.Item
                  label="Exercise Image"
                  help={
                    !formData.validImage && (
                      <Typography.Paragraph type="danger">
                        Selected image is either not in JPG, JPEG, or PNG format
                        or has a size of more than 2.5MB.
                      </Typography.Paragraph>
                    )
                  }
                >
                  <div className={!formData.validImage ? 'has-error' : ''}>
                    <ImageSelector
                      width="auto"
                      height={326}
                      ref={imgSelectorRef}
                      isUser={false}
                      defaultImageUrl={formData.base64 || formData.img}
                      src={formData.base64 || formData.img}
                      onFileSelected={onFileSelected}
                      onFileRemoved={onFileRemoved}
                      imageValidator={exerciseImgValidator}
                    />
                  </div>
                </Form.Item>
              </Col>
            </Col>
          </Row>
        </Form>
      </ModalForm>
    </Fragment>
  );
};

const mapStateToProps = (state) => ({
  visibleProfile: state.visibleProfile
});

const mapDispatchToProps = (dispatch) => ({
  getGroupExercises: (id) => dispatch(getGroupExercises(id))
});

export default connect(mapStateToProps, mapDispatchToProps)(ClinicExerciseForm);
